import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import styled, { createGlobalStyle } from 'styled-components';
import { encryptToUrl } from './utils/crypto';
import axiosInstance from './utils/axiosInstance';

const GlobalStyle = createGlobalStyle`
  :root {
    --bg: #f3f4f6;
    --card-bg: #ffffff;
    --primary: #1f2937;
    --primary-hover: #111827;
    --text: #111827;
    --muted: #6b7280;
    --border: #e5e7eb;
    --shadow: 0 18px 30px rgba(15, 23, 42, 0.08);
  }
  *, *::before, *::after { box-sizing: border-box; }
  html, body, #root { height: 100%; }
  body { margin: 0; background: var(--bg); font-family: system-ui, -apple-system, Segoe UI, Roboto, Ubuntu, Cantarell, 'Noto Sans', 'Helvetica Neue', Arial, 'Apple Color Emoji', 'Segoe UI Emoji'; color: var(--text); }
`;

const Page = styled.div`
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 40px 16px;
  background: radial-gradient(circle at top left, #e5e7eb 0, #f3f4f6 45%, #f9fafb 100%);
`;

const Container = styled.div`
  max-width: 480px;
  width: 100%;
`;

const Header = styled.div`
  text-align: center;
  margin-bottom: 24px;
`;

const LockIcon = styled.div`
  width: 56px;
  height: 56px;
  margin: 0 auto 12px;
  border-radius: 50%;
  border: 1px solid var(--border);
  background: #ffffff;
  display: grid;
  place-items: center;
  color: var(--primary);
  font-weight: 600;
  font-size: 20px;
`;

const Title = styled.h1`
  margin: 0;
  font-size: 28px;
  font-weight: 700;
  letter-spacing: -0.01em;
`;

const Version = styled.div`
  margin-top: 6px;
  color: var(--muted);
  font-weight: 600;
  font-size: 12px;
`;

const Subtitle = styled.p`
  margin: 10px 0 0;
  color: var(--muted);
  font-size: 14px;
`;

const Card = styled.div`
  background: var(--card-bg);
  border-radius: 12px;
  box-shadow: var(--shadow);
  padding: 28px 24px 24px;
  border: 1px solid var(--border);
`;

const Label = styled.label`
  display: block;
  font-size: 13px;
  color: var(--muted);
  margin-bottom: 8px;
`;

const Input = styled.input`
  width: 100%;
  height: 44px;
  border-radius: 8px;
  border: 1px solid var(--border);
  padding: 0 12px;
  font-size: 14px;
  outline: none;
  background: #ffffff;
  transition: box-shadow 0.2s, border-color 0.2s, background 0.2s;
  &:focus {
    border-color: var(--primary);
    box-shadow: 0 0 0 2px rgba(15, 23, 42, 0.18);
    background: #fff;
  }
`;

const Field = styled.div`
  & + & { margin-top: 16px; }
`;

const PasswordWrap = styled.div`
  position: relative;
`;

const ToggleBtn = styled.button`
  position: absolute;
  right: 8px;
  top: 50%;
  transform: translateY(-50%);
  height: 32px;
  width: 32px;
  border: none;
  background: transparent;
  color: var(--muted);
  cursor: pointer;
  display: grid;
  place-items: center;
  border-radius: 6px;
  transition: background 0.15s, color 0.15s;
  &:hover { background: #eef2ff; color: var(--primary); }
  &:focus { outline: 2px solid rgba(79, 70, 229, 0.35); outline-offset: 2px; }
`;

const SignIn = styled.button`
  margin-top: 20px;
  width: 100%;
  height: 44px;
  border-radius: 8px;
  border: none;
  background: var(--primary);
  color: white;
  font-weight: 600;
  font-size: 14px;
  cursor: pointer;
  letter-spacing: 0.03em;
  text-transform: uppercase;
  transition: background 0.2s, transform 0.02s, box-shadow 0.2s;
  &:hover { background: var(--primary-hover); box-shadow: 0 8px 18px rgba(15, 23, 42, 0.18); }
  &:active { transform: translateY(0.5px); }
`;

const Footer = styled.div`
  margin-top: 12px;
  text-align: center;
  color: #9ca3af;
  font-size: 12px;
`;

export default function Login() {
  const navigate = useNavigate();
  const [showPassword, setShowPassword] = useState(false);
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');

  const onSubmit = (e) => {
    e.preventDefault();
    axiosInstance
      .post(`/students/login`, { username, password }, { headers: { 'Content-Type': 'application/json' } })
      .then((resp) => {
        const data = resp?.data;
        const loginType = data?.loginType || 'student';
        const payload = {
          message: data?.message,
          apiKey: data?.apiKey,
          student: data?.student || null,
          studentId: data?.student?.studentId || null,
          loginType,
          type: loginType,
        };
        const encryptedUrl = encryptToUrl(payload);
        const valueToStore = `kaarya://${encryptedUrl}`;
        try {
          window.localStorage.setItem('kaarya_user', valueToStore);
        } catch (_) {}
        navigate('/app/select-course', { replace: true });
      })
      .catch((error) => {
        const msg = error?.response?.data?.message || error?.message || 'Login failed';
        alert(msg);
      });
  };

  return (
    <Page>
      <GlobalStyle />
      <Container>
        <Header>
          <LockIcon>MYC</LockIcon>
          <Title>MYC</Title>
          <Version>V2.0</Version>
          <Subtitle>Student portal sign-in</Subtitle>
        </Header>

        <Card>
          <form onSubmit={onSubmit}>
            <Field>
              <Label>Username</Label>
              <Input
                type="text"
                placeholder="Enter your username"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                autoComplete="username"
              />
            </Field>

            <Field>
              <Label>Password</Label>
              <PasswordWrap>
                <Input
                  type={showPassword ? 'text' : 'password'}
                  placeholder="Enter your password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  autoComplete="current-password"
                />
                <ToggleBtn
                  type="button"
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                  title={showPassword ? 'Hide password' : 'Show password'}
                  onClick={() => setShowPassword((v) => !v)}
                >
                  {showPassword ? (
                    // Eye-off icon
                    <svg
                      width="20"
                      height="20"
                      viewBox="0 0 24 24"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                      aria-hidden="true"
                    >
                      <path d="M3 3l18 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                      <path
                        d="M2.46 12.74C3.9 9.3 7.33 6.5 12 6.5c1.76 0 3.4.39 4.85 1.08"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                      <path
                        d="M21.54 11.26C20.1 14.7 16.67 17.5 12 17.5c-1.76 0-3.4-.39-4.85-1.08"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                      <circle cx="12" cy="12" r="3.5" stroke="currentColor" strokeWidth="2" />
                    </svg>
                  ) : (
                    // Eye icon
                    <svg
                      width="20"
                      height="20"
                      viewBox="0 0 24 24"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                      aria-hidden="true"
                    >
                      <path
                        d="M2.5 12c2.2-4.5 6.2-7.5 9.5-7.5s7.3 3 9.5 7.5c-2.2 4.5-6.2 7.5-9.5 7.5S4.7 16.5 2.5 12z"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinejoin="round"
                      />
                      <circle cx="12" cy="12" r="3.5" stroke="currentColor" strokeWidth="2" />
                    </svg>
                  )}
                </ToggleBtn>
              </PasswordWrap>
            </Field>

            <SignIn type="submit">Sign in</SignIn>
          </form>
        </Card>

        <Footer>© 2025 MYC. All rights reserved.</Footer>
      </Container>
    </Page>
  );
}
